import { useEffect, useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { config } from '@/services/config'
import { usePermission } from '@/features/auth/permissions'
import { createSharedSseConnection, sseEnabled } from '@/services/sse'
import { endpoints } from '@/services/endpoints'
import { useCallback, useMemo, useRef } from 'react'
import {
  anomalyDetailFromWire,
  fetchAnomalies,
  fetchAnomalyDemoFacets,
  fetchAnomaly,
  fetchSensorTagReadingPage,
  readingInstant,
  saveAnomaly,
  saveAnomalySensorTags,
} from './api'
import { subscribeToAnomalyList } from './liveStream'
/* The retired RCA hooks below also imported `isRcaRunning`, `fetchAnomalyRca`,
   `requestAnomalyRca` and `updateAnomalyRca`. All four are commented out in
   `api.js`; restoring the hooks means restoring these with them. */

/**
 * One page of anomalies, filtered and sorted server-side.
 *
 * ── Every filter is in the query key, deliberately ────────────────────────
 * Nothing is narrowed in the client. `GET /anomalies` takes status, severity,
 * the three hierarchy ids, a created_at range, a sort field and a direction, and
 * answers with the page plus the facet counts — so the server is the only thing
 * that decides which rows exist, and each combination is its own cache entry.
 * Stepping back to a filter you already used is instant; nothing recomputes.
 *
 * ── `placeholderData` keeps the table still ───────────────────────────────
 * Without it every filter change and every page step blanks the table to a
 * skeleton and repaints, so working through a list is a strobe — and the footer's
 * "Showing 1–50 of 412" briefly disagrees with an empty body. Keeping the
 * previous page on screen means the only thing that changes is the rows.
 *
 * The trade is that `isPending` is false during a refetch (there IS data, just
 * the previous filter's), so the screen reads `isPlaceholderData` to dim rather
 * than `isFetching` — which would also fire on a background revalidation and
 * flicker a table nobody touched.
 *
 * ── It polls ──────────────────────────────────────────────────────────────
 * Detections arrive while somebody is looking at the screen, and a list that
 * only moves when the page is reloaded is a list that is wrong for as long as
 * nobody reloads it. So this refetches on `ANOMALY_POLL_INTERVAL_MS` for as long
 * as a component holding the hook is mounted, and stops the moment one is not —
 * which is exactly the "while the user is on the screen" rule, expressed as
 * mounting rather than as a route check somebody has to keep in step.
 *
 * `placeholderData` is what makes that bearable at this cadence: the same key
 * refetching in place swaps the rows and touches nothing else, so a two-second
 * poll under a reader's cursor is not a two-second flash.
 *
 * `refetchIntervalInBackground` is left off, as it is for signals. A tab nobody
 * is looking at stops asking, and picks up again on focus.
 */
export function useAnomalies(filters, { enabled = true, pollMs = false } = {}) {
  return useQuery({
    queryKey: queryKeys.anomalies(filters),
    queryFn: ({ signal }) => fetchAnomalies(filters, signal),
    placeholderData: (previous) => previous,
    // The list screen always wants its rows; the severity band only wants them
    // when there is a server to answer. A request nobody renders is a request
    // nobody can see.
    enabled,
    /**
     * Zero, deliberately — the same reason `useSignals` sets it.
     *
     * The default 30s staleness is longer than the interval, and while a poll
     * does fire regardless of it, everything else that revalidates would be
     * served the cached page instead: a remount, a scope change, a return to the
     * tab. On a surface asking every two seconds because the answer is expected
     * to have moved, "fresh for thirty" is not true of any of it.
     */
    staleTime: 0,
    /**
     * OFF by default. Liveness comes from `useAnomalyListStream` now.
     *
     * ── The default is inverted, deliberately ───────────────────────────
     * This used to be `poll: true` with two of five call sites opting out —
     * which meant a new picker whose author forgot `poll: false` polled the
     * whole estate every two seconds forever, silently and for nothing. Now
     * nothing polls unless it says so, and the two screens that want liveness
     * subscribe to the stream instead.
     *
     * `pollMs` remains for one case only: the stream's FALLBACK. If SSE cannot
     * connect — a proxy that buffers `text/event-stream` is the usual cause —
     * the list screen turns on a slow poll so the screen degrades to what it
     * used to do rather than going silent. `false` rather than `0` when off:
     * TanStack reads 0 as "as fast as possible".
     */
    refetchInterval: pollMs || false,
  })
}

/**
 * Two seconds, for the surface that is expected to move.
 *
 * Kept as the ceiling on the stream's own coalescing window rather than as a
 * request interval — see `LIST_COALESCE_MAX_MS`. Nothing polls at this rate any
 * more.
 */
export const ANOMALY_POLL_INTERVAL_MS = 2000

/**
 * The fallback rate, used only when the stream has given up.
 *
 * Ten seconds and not two: this is a safety net, and a network that eats SSE
 * should not silently restore the full cost of the arrangement the stream was
 * built to replace.
 */
export const ANOMALY_FALLBACK_POLL_INTERVAL_MS = 10_000

/**
 * Whether this reader may see anomalies at all.
 *
 * Every anomaly route — list, detail, update, sensor tags and all three streams
 * — takes `anomalies.manage` and nothing else. There is no `anomalies.view`, so
 * reading and writing are one permission and this hook answers one question.
 * `is_root` bypasses, as it does server-side.
 */
export function useAnomalyAccess() {
  const manage = usePermission('anomalies.manage')
  return { isPending: manage.isPending, granted: manage.granted }
}

/**
 * Keep the anomaly list live.
 *
 * Opt-in: mounting this hook is what makes a list live, and only the list screen
 * and the app shell's severity band do. The two anomaly PICKERS — on the logbook
 * and work-order forms — deliberately do not, because a dialog that opens for
 * thirty seconds has no business holding a stream.
 *
 * The connection is shared and ref-counted, so both callers together open one
 * socket. See `liveStream.js` for why the event is a trigger to refetch rather
 * than a row to apply.
 *
 * @returns {{status: 'idle'|'connecting'|'live'|'retrying'|'failed'}}
 */
export function useAnomalyListStream({ enabled = true } = {}) {
  const client = useQueryClient()
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    if (!enabled || !sseEnabled()) return undefined
    return subscribeToAnomalyList(
      () => client.invalidateQueries({ queryKey: queryKeys.anomaliesList }),
      setStatus,
    )
  }, [enabled, client])

  return { status }
}

/**
 * DEMO. The facet tiles the server cannot count.
 *
 * Gated at the hook rather than at the JSX, per DEMO-MODE §2.2: with the flag off
 * the screen makes no request for a row it is not showing, and the tiles can be
 * moved or refactored without anyone remembering to re-check the flag.
 *
 * `staleTime: Infinity` because a fixture does not change. It deliberately does
 * NOT poll alongside the list beside it — two seconds of re-fetching a constant
 * would be the demo layer costing more requests than the real one.
 */
export function useAnomalyDemoFacets() {
  return useQuery({
    queryKey: queryKeys.anomalyDemoFacets,
    queryFn: ({ signal }) => fetchAnomalyDemoFacets(signal),
    enabled: config.demoMode,
    staleTime: Infinity,
  })
}

/**
 * One anomaly.
 *
 * `staleTime: 0` for the reason every edit surface in this app sets it: the save
 * is a full replace of `severity` AND `status`, so a status change built from a
 * cached copy writes back whatever severity that copy happened to hold.
 *
 * `gcTime: 0` is now LOAD-BEARING in a way it was not before. `useAnomalyStream`
 * writes into this key with `setQueryData`, and a write into an entry with no
 * observers would be collected on the next tick — harmless only because the
 * stream's lifetime is exactly this hook's: both live on the detail page and
 * both unmount with it. Anything that opens the stream elsewhere has to revisit
 * this line.
 */
export function useAnomaly(anomalyId) {
  return useQuery({
    queryKey: queryKeys.anomaly(anomalyId),
    queryFn: ({ signal }) => fetchAnomaly(anomalyId, signal),
    enabled: Boolean(anomalyId),
    staleTime: 0,
    gcTime: 0,
  })
}

/**
 * One pinned sensor tag's readings, a page at a time.
 *
 * ── Lazy, where this used to fetch the whole window on mount ───────────
 * It looped until the window was exhausted — up to eight requests per card, and
 * a board of four cards opened with thirty-two in flight, almost all of them for
 * readings nobody had panned to. Now the card draws the first page and the next
 * one is asked for when the reader reaches the end of what is loaded. A 54-second
 * window usually IS one page, so the common case went from several requests to
 * exactly one.
 *
 * ── `useInfiniteQuery`, because the pages are a sequence and not a set ──
 * The cursor is the server's own `to` and each page's existence depends on the
 * one before it, so the pages cannot be fetched out of order or in parallel.
 * `getNextPageParam` returns `undefined` on a short page, which is what makes
 * `hasNextPage` the honest answer to "is there more of this window".
 *
 * ── Held forever, for the same reason as before ────────────────────────
 * The window is closed and the readings in it were written by the broker before
 * the detector ever fired; no endpoint can alter them. `staleTime: Infinity`
 * rather than a number, because any number would assert that they might change.
 * Pages already fetched therefore survive switching tabs, expanding a chart and
 * coming back to it.
 *
 * @returns readings flattened across the pages fetched so far, plus the controls
 *   the chart's forward arrow needs.
 */
export function useSensorTagReadings(anomalyId, sensorTagId, { enabled = true } = {}) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.anomalySensorTagReadings(anomalyId, sensorTagId),
    queryFn: ({ pageParam, signal }) =>
      fetchSensorTagReadingPage({ anomalyId, sensorTagId, from: pageParam }, signal),
    /* `null`, not `undefined`: TanStack treats an undefined param as "no page to
       fetch". Null is a value, and `fetchSensorTagReadingPage` reads it as "start
       at the window's own beginning". */
    initialPageParam: null,
    getNextPageParam: (last) => last?.next ?? undefined,
    enabled: enabled && Boolean(anomalyId) && Boolean(sensorTagId),
    staleTime: Infinity,
  })

  /* Flattened once per change rather than on every render: the chart slices,
     scans and plots this array several times per paint. */
  const readings = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.readings),
    [query.data],
  )

  /* Every page of the window, for an export. The chart only fetches what has
     been panned to, and a CSV of that would be silently cut off wherever the
     reader stopped. Pages fetched here land in the same cache, so the chart
     behind it is fully loaded afterwards too. */
  const { data, hasNextPage, fetchNextPage } = query
  const loadAll = useCallback(async () => {
    let result = { data, hasNextPage }
    while (result.hasNextPage) {
      result = await fetchNextPage()
      if (result.isError) throw result.error
    }
    return (result.data?.pages ?? []).flatMap((page) => page.readings)
  }, [data, hasNextPage, fetchNextPage])

  return {
    readings,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: query.fetchNextPage,
    loadAll,
  }
}

/**
 * Keep one anomaly live.
 *
 * ── This event carries the whole record, so it is applied, not a trigger ──
 * Unlike the list stream, `GET /anomalies/{id}/stream` pushes the full
 * `AnomalyOut` — the same shape the detail read returns — so it is written
 * straight into the cache and costs no request at all.
 *
 * It also wakes on the anomaly's CHILD rows: sensor tags, similarities and
 * document references each notify the same channel. That is what makes the
 * linked records on the Action tab live without a second subscription.
 *
 * ── The recency guard is not defensive programming ───────────────────────
 * On mount, `useAnomaly` fires a GET while this stream connects. If an event
 * lands while that GET is still in flight, the GET's older response resolves
 * afterwards and wins — the change appears and then vanishes, which reads as a
 * flicker nobody can reproduce. Comparing `updatedAt` makes the write
 * idempotent against that race.
 *
 * ── An empty event means the row is gone or out of scope ─────────────────
 * Invalidate rather than writing `{}` over a good record: the GET is the
 * authoritative answer to which of the two it was, and a 404 lands the page on
 * the not-found path it already has.
 */
export function useAnomalyStream(anomalyId, { enabled = true } = {}) {
  const client = useQueryClient()
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    if (!anomalyId || !enabled || !sseEnabled()) return undefined

    const connection = createSharedSseConnection({
      path: endpoints.anomalies.detailStream(anomalyId),
      // Not shared and not lingered: one detail page, one socket, closed on
      // unmount. Holding it open would spend one of the six connections a
      // browser gives an HTTP/1.1 origin on a page nobody is looking at.
      lingerMs: 0,
      onEvent: (event) => {
        if (!event?.id) {
          client.invalidateQueries({ queryKey: queryKeys.anomaly(anomalyId) })
          client.invalidateQueries({ queryKey: queryKeys.anomaliesList })
          return
        }
        const incoming = anomalyDetailFromWire(event)
        client.setQueryData(queryKeys.anomaly(anomalyId), (previous) =>
          previous?.updatedAt && incoming.updatedAt && previous.updatedAt > incoming.updatedAt
            ? previous
            : incoming,
        )
        /* The row in the list behind this screen moved too — a status change
           shifts `counts.status`, which no client can recompute. */
        client.invalidateQueries({ queryKey: queryKeys.anomaliesList })
      },
      /* A reconnect is a gap, and a gap is news: there is no replay, so refetch
         rather than assume nothing happened while the socket was down. */
      onOpen: ({ reconnected }) => {
        if (reconnected) client.invalidateQueries({ queryKey: queryKeys.anomaly(anomalyId) })
      },
      onStatus: setStatus,
    })

    return connection.subscribe()
  }, [anomalyId, enabled, client])

  return { status }
}

/*
 * ── The RETIRED RCA hooks ────────────────────────────────────────────────
 * `useAnomalyRca`, `useRequestAnomalyRca` and `useUpdateAnomalyRca` all called
 * routes that no longer exist — the RCA sub-resource was merged into
 * `Anomaly.content` and its endpoints removed. Kept commented rather than
 * deleted, per the standing recipe.
 *
 * The polling machinery below is worth keeping legible for whoever wires the
 * copilot request: `refetchInterval` as a FUNCTION reading the query's own data
 * is what let one `useQuery` both start and stop its own poll, and the
 * `refetchIntervalInBackground` exception — the only one in this app — was
 * argued for on the grounds that an analysis takes minutes and people switch
 * away while it runs. Both were right and neither has anywhere to apply now.
 *
 * `RCA_POLL_CEILING_MS` went with them. It bounded a "still analysing" state
 * that no longer exists: with no request endpoint there is nothing to be waiting
 * on, so there is no stall to give up on.
 */

// /**
//  * One anomaly's root-cause analysis, or `null` when none has been requested.
//  *
//  * ── `null` is a successful answer here ────────────────────────────────────
//  * `fetchAnomalyRca` converts the endpoint's 404 into `null`, so `isError` means
//  * something actually went wrong and `data === null` means nobody has asked for
//  * an analysis yet. The screen can therefore branch on the data rather than on an
//  * error object, which is the difference between an empty state and a red panel on
//  * every new anomaly.
//  *
//  * That also means `null` must be distinguished from "still loading" by
//  * `isPending`, not by a falsy check — `data` is `undefined` before the request
//  * lands and `null` after it lands with nothing there.
//  */
// export function useAnomalyRca(anomalyId, { poll = false } = {}) {
//   return useQuery({
//     queryKey: queryKeys.anomalyRca(anomalyId),
//     queryFn: ({ signal }) => fetchAnomalyRca(anomalyId, signal),
//     enabled: Boolean(anomalyId),
//     /**
//      * Poll while an analysis has been requested and has not arrived.
//      *
//      * A function, not a number, so the decision reads the query's OWN latest data
//      * — which is what lets one `useQuery` call both start and stop the poll. The
//      * alternative is to derive "still queued" in the component and feed it back in,
//      * which needs the data before the hook that fetches it, so it ends up as two
//      * hook calls on one key with two conflicting timers.
//      *
//      * The test is `isRcaRunning` — `queued` AND no content. It was `!hasRcaContent`
//      * alone, which polled a `pending` row forever: nobody had requested that one, so
//      * nothing was ever going to arrive and the screen sat there asking. A `draft`
//      * with empty content also stops the poll now, correctly — `PATCH .../rca` sets
//      * that, and a status a person set by hand is not a job to wait on.
//      *
//      * `poll` is the caller's ceiling — it owns the "stopped watching" state, because
//      * that is a UI decision. `false` rather than `0` when idle: TanStack reads 0 as
//      * "as fast as possible".
//      */
//     refetchInterval: (query) =>
//       poll && isRcaRunning(query.state.data) ? RCA_POLL_INTERVAL_MS : false,
//     /**
//      * Keeps polling with the tab in the background.
//      *
//      * Normally the wrong default — a backgrounded tab quietly burning requests is
//      * how a polling bug hides. It is right here and only here: analysis takes up to
//      * a couple of minutes, somebody starts it and switches away, and the point of
//      * the flow is that it is finished when they come back. The caller's ceiling is
//      * what keeps it bounded.
//      */
//     refetchIntervalInBackground: poll,
//   })
// }
//
// /** Two seconds between checks, as specified. */
// export const RCA_POLL_INTERVAL_MS = 2000
//
// /** Two minutes of watching before the screen stops claiming to watch. */
// export const RCA_POLL_CEILING_MS = 120_000
//
// /**
//  * Ask for an analysis.
//  *
//  * Invalidates only this anomaly's RCA. Nothing else moves: requesting an analysis
//  * does not change the anomaly's own record, its status, or any count on the list
//  * — so sweeping `['anomalies']` would refetch a paginated list to show a change
//  * that is not in it.
//  *
//  * `retry: false` even though the endpoint is idempotent. A retry cannot create a
//  * second row, but it also cannot help: the failures worth retrying are transport
//  * faults, and the query client's own policy already covers those on the read.
//  */
// export function useRequestAnomalyRca(anomalyId) {
//   const client = useQueryClient()
//
//   return useMutation({
//     mutationFn: () => requestAnomalyRca(anomalyId),
//     retry: false,
//     onSuccess: (rca) => {
//       // Seeded rather than merely invalidated, so the panel switches to "queued"
//       // on the response instead of blanking for one more round trip.
//       client.setQueryData(queryKeys.anomalyRca(anomalyId), rca)
//     },
//   })
// }
//
// /**
//  * Save an edited analysis, or approve it.
//  *
//  * Writes the response straight into the cache rather than invalidating, for a
//  * reason specific to this screen: the RCA query may be polling, and an
//  * invalidation mid-poll can land the fetch that was already in flight — carrying
//  * pre-save content — after this one, so the editor would appear to lose the save
//  * and then recover it. Seeding removes the race entirely.
//  *
//  * `['anomalies']` is swept as well, because `rca_status` rides along on every list
//  * item: approving a draft has to move the badge on the card behind this screen.
//  */
// export function useUpdateAnomalyRca(anomalyId) {
//   const client = useQueryClient()
//
//   return useMutation({
//     mutationFn: (input) => updateAnomalyRca({ anomalyId, ...input }),
//     retry: false,
//     onSuccess: (rca) => {
//       client.setQueryData(queryKeys.anomalyRca(anomalyId), rca)
//       client.invalidateQueries({ queryKey: ['anomalies'] })
//     },
//   })
// }

/**
 * Change a severity or a status.
 *
 * Not a title: `AnomalyUpdate` has no such field any more — see `saveAnomaly`.
 * And a FULL replace, so every caller sends both values, including the one it is
 * not changing.
 *
 * `retry: false`: a retry that lands after someone else's change would overwrite
 * it with a value this client chose before seeing theirs. Same call this
 * codebase makes on every other write.
 */
export function useSaveAnomaly() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: saveAnomaly,
    retry: false,
    onSuccess: (result) => {
      /**
       * ── Seed the detail record; never remove it ──────────────────────────
       * This used to `removeQueries` the detail key, which was harmless when only
       * the list screen existed and nothing was watching that entry. With the detail
       * page mounted it is a bug: removal wipes the cache entry the screen is
       * observing and does NOT schedule a fetch for an already-mounted observer, so
       * the header keeps rendering its last value until something remounts it. The
       * symptom is a status that changes on the server and only appears after a
       * page refresh.
       *
       * `PUT /anomalies/{id}` answers with the whole updated record — the same
       * `_to_out` shape `GET /anomalies/{id}` returns — so writing it straight in is
       * both correct and one request cheaper than invalidating. The stream will
       * deliver the same record a moment later; the recency guard in
       * `useAnomalyStream` is what stops the two fighting.
       */
      if (result?.id) client.setQueryData(queryKeys.anomaly(result.id), result)

      /**
       * Only the list. A status change moves the row between status-filtered pages
       * and moves `counts.status`, which rides along in the same response, so every
       * cached page is wrong — but the detail entry above is already right, and
       * sweeping the whole `['anomalies']` root would refetch it for nothing.
       */
      client.invalidateQueries({ queryKey: queryKeys.anomaliesList })
    },
  })
}

/**
 * Pin or remove sensor tags on one anomaly.
 *
 * ── The caller hands over the WHOLE set, because the endpoint takes one ──
 * `PUT /anomalies/{id}/sensor-tags` delta-syncs against its body: a tag left out
 * is soft-deleted. So this hook deliberately does NOT take "the tag to add" and
 * build a body around a list of its own — the caller passes the complete target
 * set, derived from the record it is rendering, and the hazard stays visible at
 * the call site instead of being hidden in here.
 *
 * ── The response is the set, not the anomaly ────────────────────────────
 * `set_anomaly_sensor_tags` answers with `_resolve_sensor_tags` alone, so unlike
 * `useSaveAnomaly` there is no whole record to write into the cache. The detail
 * entry is invalidated instead, which is one request and is also what the detail
 * SSE stream would have caused a moment later — it wakes on this anomaly's child
 * rows, sensor tags included.
 *
 * The list is NOT invalidated: `AnomalyListItem` carries no sensor tags (and so
 * no `is_source`), so no cached page can have changed.
 *
 * `retry: false`, like every other write here: a retry of a full-replace body
 * built before somebody else's change would delete their tag and report success.
 */
export function useSaveAnomalySensorTags() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: saveAnomalySensorTags,
    retry: false,
    /* Settled, not success: a refusal is often a STALE body. The engine can add
       a source tag between this read and the write, and a body built without it
       is a 400 ("Source tags can't be removed.") — the next attempt has to be
       built from the set as it now is, not from the one on screen. */
    onSettled: (_result, _error, variables) => {
      client.invalidateQueries({ queryKey: queryKeys.anomaly(variables.anomalyId) })
    },
  })
}

/**
 * One sensor tag's LIVE readings, as a rolling buffer.
 *
 * ── Live and history are different endpoints answering different questions ──
 * `fetchSensorTagReadings` reads `[window_start, window_end]` — the event, closed
 * and immutable. This reads `GET .../sensor-tags/{tag}/stream`, which pushes
 * readings as the broker delivers them and never ends. A card showing one is not
 * showing a different range of the other; it is showing a different subject.
 *
 * ── The server backfills, so a live card is not born empty ──────────────
 * `_subscribe` drains its per-tag buffer into the new queue immediately, which is
 * `sensor_reading_buffer_window_s` (60s) of recent history. So the first paint
 * carries a minute of trace rather than one dot that grows. Nothing else replays:
 * a RECONNECT gets the same 60s, which is why `onOpen` clears rather than
 * appends — otherwise the overlap is drawn twice, as a trace that doubles back.
 *
 * ── One connection, because only one card may be live ───────────────────
 * `try_acquire(user.id, "anomaly_sensor_tags", 4)` caps concurrent streams per
 * user, and the 5th is a `ValidationAppError` rather than a queue. This hook is
 * built for exactly one subscriber at a time — the card the reader selected —
 * so the cap is not reachable from this screen and the limit never has to be
 * explained to anybody.
 *
 * @param {string} anomalyId
 * @param {string} sensorTagId
 * @param {{enabled?: boolean, keepMs?: number}} options `keepMs` trims the buffer
 *   to the last N milliseconds of READING time — the duration chips above the
 *   chart — not of wall-clock time, so a sensor that pauses does not erase its
 *   own trace while nothing is arriving.
 */
export function useLiveSensorTagReadings(
  anomalyId,
  sensorTagId,
  { enabled = true, keepMs = 120_000 } = {},
) {
  const [readings, setReadings] = useState([])
  const [status, setStatus] = useState('idle')

  /* The trim window, read inside the event handler without being a dependency of
     the effect that opens the socket. Changing a duration chip must re-trim the
     buffer, not tear down and rebuild the stream — so it is written in an effect
     of its own rather than during render. */
  const keepRef = useRef(keepMs)
  useEffect(() => {
    keepRef.current = keepMs
  }, [keepMs])

  const live = enabled && Boolean(anomalyId) && Boolean(sensorTagId) && sseEnabled()

  useEffect(() => {
    if (!live) return undefined

    const connection = createSharedSseConnection({
      path: endpoints.anomalies.sensorTagStream(anomalyId, sensorTagId),
      /* No linger. One live card at a time is the whole design, and a lingering
         socket would hold one of the four budgeted connections open for a tag
         nobody is watching — which the NEXT selection would then be refused. */
      lingerMs: 0,
      onEvent: (event) => {
        if (event?.event !== 'reading') return
        const value = Number(event.value)
        if (!Number.isFinite(value)) return

        const at = readingInstant(event.timestamp)
        setReadings((previous) => {
          const next = [...previous, { at, value }]
          /* Trimmed against the NEWEST reading's own stamp. Trimming against
             `Date.now()` instead would empty the chart whenever the broker fell
             behind — which is the moment somebody is most likely to be watching
             it. */
          const newest = at ? Date.parse(at) : NaN
          if (!Number.isFinite(newest)) return next
          const floor = newest - keepRef.current
          return next.filter((reading) => {
            const stamp = reading.at ? Date.parse(reading.at) : NaN
            return !Number.isFinite(stamp) || stamp >= floor
          })
        })
      },
      /* Cleared on EVERY open, not only on a reconnect. `_subscribe` drains the
         server's 60s per-tag buffer into each new queue, so an open always
         replays; appending it to what is already here draws the overlap twice,
         as a trace that folds back on itself. Clearing here also covers the
         switch from one tag to another, without a `setState` in the effect body
         that would cascade a render on every mount. */
      onOpen: () => setReadings([]),
      onStatus: setStatus,
    })

    return connection.subscribe()
  }, [live, anomalyId, sensorTagId])

  /* `EMPTY` and not `[]`: a fresh array every render is a new identity, and the
     chart below memoises on this value. */
  return { readings: live ? readings : EMPTY, status }
}

/** One frozen empty list, so a disabled live hook has a stable identity. */
const EMPTY = []
